import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { whole } from '@/lib/format';
import { Truncated } from './parts';

type Ranking = Extract<WireArtifact, { kind: 'ranking' }>;

/** Top names by number of loads, as bars against the leader — the chart's language. */
export function RankingArtifact({ artifact }: { artifact: Ranking }): JSX.Element {
  const most = Math.max(1, ...artifact.rows.map((r) => r.count));
  const what = artifact.dimension === 'customer' ? 'customers' : 'trucks';
  return (
    <div className="flex flex-col gap-3.5">
      <div className="border-line bg-surf flex flex-col gap-2.5 rounded-xl border px-3.5 py-3.5">
        {artifact.rows.map((row, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-[12.5px]">{row.label}</span>
              <span className="flex-none font-mono text-[12.5px] tabular-nums">
                {whole(row.count)}
              </span>
            </div>
            <div className="bg-surf2 h-1.5 overflow-hidden rounded-full">
              <div
                className="h-full rounded-full transition-[width] duration-700"
                style={{
                  width: `${Math.max(3, (row.count / most) * 100)}%`,
                  background:
                    row.count === most
                      ? 'var(--acc)'
                      : 'color-mix(in oklch, var(--acc) 45%, transparent)',
                }}
              />
            </div>
          </div>
        ))}
        {artifact.rows.length === 0 && (
          <span className="text-mute text-[13px]">Nothing to rank.</span>
        )}
      </div>
      <div className="text-mute text-[13px] leading-normal">
        Loads per {artifact.dimension}, from the records the answer used.
      </div>
      {artifact.truncated && <Truncated what={`top ${what}`} />}
    </div>
  );
}
